import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Column } from "react-table";

import {
    BLANCCO_TOKEN_ID,
    createProductIdToNameMap,
    createSortedLicenseTypes,
    FEATURE_LICENSES,
    isSubscriptionLicense,
} from "./common";
import { isLicenseExpired, statusBadgeTitle } from "./delivery-history/LicenseSelectionForm";
import commonStyle from "./license-keys-table.scss";
import tokenTableStyle from "./token-pricing-tier-view.scss";
import Chevron from "components/icons/Chevron";
import Info from "components/icons/Info";
import BundleTokenPricingView from "components/licenses/BundleTokenPricingView";
import deliveryStyle from "components/licenses/delivery-history/add-license-delivery.scss";
import LicenseTierBanner from "components/licenses/license-tier-banner/LicenseTierBanner";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import SearchView from "components/search/SearchView";
import StatusBadge from "components/status-badge/StatusBadge";
import DateCell from "components/table/DateCell";
import Table, { deriveColumnWidth } from "components/table/Table";
import tableStyle from "components/table/table.scss";
import TextWithTooltip from "components/table/TextWithTooltip";
import { deduceTierLicenses } from "components/tenants/add-tenant/AddTenantForm";
import style from "components/tenants/tenants.scss";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { LicenseData, LicenseKeyCursor, Licenses } from "domain/licenses";
import { LicensingModel } from "domain/tenants";
import { licenseService, ProductRateList, ProductToRateList } from "services/licenses/LicenseService";
import {
    getCurrentTenantDetails,
    hasTenantCookie,
    isLicensingModelBundleWithToken,
    isUserParentInternal,
} from "services/tenants/tenantCookieService";
import { store, StoreState } from "store";
import {
    setHasBmsAllLicenses,
    setHasBmsBbtiLicenses,
    setHasBmsInsuranceLicenses,
    setHasBmsLicenses,
    setHasBmsNtfLicenses,
    setHasBmsValidationLicenses,
    setHasUsdkLicenses,
} from "store/license";
import buttonStyle from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { isExpired, roundToOneDecimal } from "utils/format";
import { logger } from "utils/logging";
import { RepositoryKey } from "utils/repository";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    initialLicenses?: Licenses;
    requestFailureMessage?: string;
    scrollPosition?: number;
    tokenRates?: ProductRateList[];
    own: boolean;
}

interface TableState {
    licenses: LicenseData[];
    cursor: LicenseKeyCursor | null;
    scrollPosition?: number;
}

export interface ProductToRates {
    productId: string | undefined;
    rate: string;
}

export interface FetchedLicenseData {
    product: string | undefined;
    license: string | undefined;
    total: number;
    available: number;
    expirationDate: string;
    type: string;
    licenseType: string;
    isInvalid?: boolean;
    isFeatureLicensePresent: boolean;
    used?: number;
    licenses?: LicenseData[];
    overallRemaining?: number;
    assigned?: number;
}

export const createProductRates = (
    rates: ProductRateList[],
    tierLicenses: string[],
    productNames: Map<string, string>
): ProductToRates[] => {
    return rates
        .filter((each) => productNames.has(each.productId) && tierLicenses.includes(each.productId))
        .map((each) => ({
            productId: productNames.get(each.productId) || each.productId,
            rate: Number.isInteger(each.rate) ? each.rate.toFixed(1) : each.rate.toString(),
        }))
        .sort((first, second) => first.productId?.localeCompare(second.productId));
};

const mapState = (state: StoreState) => ({
    hasBmsLicenses: state.licensesReducer.hasBmsLicenses,
    user: state.userReducer.user,
});
const connector = connect(mapState, {
    setHasBmsLicenses,
    setHasBmsValidationLicenses,
    setHasBmsBbtiLicenses,
    setHasBmsInsuranceLicenses,
    setHasBmsNtfLicenses,
    setHasBmsAllLicenses,
    setHasUsdkLicenses,
});

const LicensesTable = (rawProps: Props & ConnectedProps<typeof connector>) => {
    const props = { initialLicenses: { licenseData: [], cursor: null }, requestFailureMessage: "", ...rawProps };
    const { t } = useTranslation();
    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    const deduceLicenseTotalValue = (licenseData: LicenseData, hasBlanccoTokens?: boolean) => {
        if (licenseData.licenseType === "feature" || licenseData.licenseType === "subscription") {
            return !isUserParentInternal() || hasTenantCookie()
                ? t("Licenses.subscriptionLicenseRemaining", { remainingSubscriptionLicense: licenseData.total })
                : t("Licenses.subscription");
        }
        return hasBlanccoTokens ? licenseData.total.toFixed(1) : licenseData.total;
    };

    const ALL_PRODUCTS = createProductIdToNameMap();
    const theme = useTheme();

    const createDropdownArrow = (isExpanded: boolean) => {
        const theme = store.getState();

        return (
            <Chevron
                color={theme.themeReducer.theme.iconFillColor}
                className={classNames(style.toggleArrow, style.right, { [style.down]: isExpanded })}
                width={20}
                height={20}
            />
        );
    };

    const deduceLicenseAvailability = (licenseData: LicenseData, hasBlanccoTokens?: boolean) => {
        if (licenseData.licenseType === "feature" || licenseData.licenseType === "subscription") {
            if (licenseData.isInvalid) {
                return t("Licenses.unavailable");
            } else {
                return t("Licenses.subscription");
            }
        }
        return hasBlanccoTokens && Number.isInteger(licenseData.available)
            ? licenseData.available.toFixed(1).toString()
            : licenseData.available.toString();
    };

    const deduceLicenseUsedValue = (licenseData: LicenseData, hasBlanccoTokens?: boolean) => {
        if (licenseData.licenseType === "feature" || licenseData.licenseType === "subscription") {
            return !isUserParentInternal() || hasTenantCookie()
                ? t("Licenses.subscriptionLicenseRemaining", {
                      remainingSubscriptionLicense: licenseData.total - licenseData.available,
                  })
                : t("Licenses.subscription");
        }
        const usedValue = roundToOneDecimal(licenseData.total - licenseData.available);
        return hasBlanccoTokens && Number.isInteger(usedValue) ? usedValue.toFixed(1).toString() : usedValue.toString();
    };

    const deduceLicenseOverallAvailableValue = (licenseData: LicenseData, hasBlanccoTokens?: boolean) => {
        if (licenseData.licenseType === "feature" || licenseData.licenseType === "subscription") {
            return !isUserParentInternal() || hasTenantCookie()
                ? t("Licenses.subscriptionLicenseRemaining", { remainingSubscriptionLicense: licenseData.available })
                : t("Licenses.subscription");
        }
        return hasBlanccoTokens && Number.isInteger(licenseData.overallRemaining)
            ? licenseData.overallRemaining?.toFixed(1).toString()
            : licenseData.overallRemaining?.toString();
    };

    const tenantDetails = getCurrentTenantDetails();
    const [rateCardLicensesAndRates, setRateCardLicensesAndRates] = React.useState<ProductRateList[]>([]);
    const [loadedTierLicenses, setLoadedTierLicenses] = React.useState<string[]>();
    const deducedTierLicenses = loadedTierLicenses ?? [];
    const tokenRates = () => {
        const tokenRateList: ProductRateList[] = [];
        const onlyProducts: string[] = [];
        const abortController = new AbortController();
        licenseService
            .fetchProductTokenRate(abortController, tenantDetails.uuid)
            .then((rateList: ProductToRateList[]) => {
                if (rateList.length > 0) {
                    rateList[0].productToRate.forEach((product) => {
                        tokenRateList.push(product);
                        onlyProducts.push(product.productId);
                    });
                }
                setRateCardLicensesAndRates(tokenRateList);
                const licenses = deduceTierLicenses(tenantDetails.tenantTier, true, true, onlyProducts);
                setLoadedTierLicenses(licenses);
            })
            .catch(() => {
                logger.warn("Failed to fetch token rates; showing tier licenses without rate card filtering");
                setLoadedTierLicenses(deduceTierLicenses(tenantDetails.tenantTier, true, true));
            });
    };

    const placeholderObjects: LicenseData[] = [];

    const commonLicenses = tenantDetails.licensingModel == LicensingModel.BUNDLE_WITH_TOKEN && deducedTierLicenses;

    const createPlaceholderTableRows = (licenseDetails: FetchedLicenseData) => {
        if (licenseDetails.product) {
            const licenses: LicenseData = {
                product: licenseDetails.product,
                license: licenseDetails.product,
                total: licenseDetails.total,
                available: licenseDetails.available,
                expirationDate: licenseDetails.expirationDate,
                type: licenseDetails.type,
                licenseType: licenseDetails.licenseType,
                isInvalid: false,
                isFeatureLicensePresent: licenseDetails.isFeatureLicensePresent,
            };
            placeholderObjects.push(licenses);
        }
    };

    const convertToRows = (data: LicenseData[]) => {
        state.licenses.forEach((each) => {
            if (each.type == BLANCCO_TOKEN_ID) {
                each.subLicenses = data;
            }
        });
        setLicensesData(state);
    };

    const deduceAvailabilityTooltip = (licenseData: LicenseData, hasBlanccoTokens?: boolean) => {
        if (isExpired(licenseData.expirationDate)) {
            return t("Licenses.expired");
        }
        if (licenseData.licenseType === "feature" || licenseData.licenseType === "subscription") {
            if (licenseData.available < 1) {
                return t("Licenses.adjusted");
            }
            return t("Licenses.subscription");
        }
        return hasBlanccoTokens && Number.isInteger(licenseData.available)
            ? licenseData.available.toFixed(1).toString()
            : licenseData.available.toString();
    };

    const deduceUsesLeft = (tokensAvailable: number, product: string, hasBlanccoTokens?: boolean) => {
        if (rateCardLicensesAndRates.some((each) => each.productId === product && Number(each.rate) === 0)) {
            return t("Licenses.unlimited");
        }
        let usesLeft = tokensAvailable;
        rateCardLicensesAndRates.forEach((each) => {
            const tokenRate = Number(each.rate);
            if (each.productId === product) {
                usesLeft = roundToOneDecimal(tokensAvailable / tokenRate);
            }
        });
        return hasBlanccoTokens ? Math.floor(usesLeft).toString() : usesLeft.toString();
    };

    const [tokenPricingTierModalVisible, setTokenPricingTierModalVisible] = React.useState(false);

    const createColumns = (): Array<Column<LicenseData>> => {
        const columns: Array<Column<LicenseData>> = [];
        const hasBlanccoTokens = deducedTierLicenses.length > 0;
        columns.push({
            Header: () => <TextWithTooltip text={t("Licenses.products")} key="1" />,
            accessor: "product",
            Cell: ({ row }) => {
                const isSubRow = row.depth > 0;
                const isBlanccoCommonLicense = row.original.type == BLANCCO_TOKEN_ID;
                const isParentLicense = commonLicenses && !isSubRow && deducedTierLicenses && isBlanccoCommonLicense;
                return (
                    <>
                        <div
                            className={classNames(style.alignment, {
                                [tableStyle.subRowBorder]: isSubRow || (row.depth === 0 && row.isExpanded),
                                [tableStyle.subRowNameCell]: isSubRow,
                                [tableStyle.subRows]: isSubRow && deducedTierLicenses,
                                [tableStyle.addOnLicenses]: !isParentLicense,
                                [tableStyle.expandedRow]: row.isExpanded,
                            })}
                        >
                            {isSubRow && (
                                <div className={tableStyle.nameCellConnectorLines}>
                                    <span></span>
                                </div>
                            )}
                            {isParentLicense && (
                                <button
                                    onClick={() => {
                                        if (row.subRows.length === 0) {
                                            createSortedLicenseTypes()
                                                .filter((license) => deducedTierLicenses.includes(license.productId))
                                                .forEach(({ productId: license }) => {
                                                    if (ALL_PRODUCTS.get(license)) {
                                                        const subRowLicenses = {
                                                            product: ALL_PRODUCTS.get(license),
                                                            license: ALL_PRODUCTS.get(license),
                                                            total: row.original.total,
                                                            available: row.original.available,
                                                            expirationDate: row.original.expirationDate,
                                                            type: license,
                                                            licenseType: row.original.licenseType,
                                                            isInvalid: false,
                                                            isFeatureLicensePresent:
                                                                !(
                                                                    isSubscriptionLicense(license) ||
                                                                    FEATURE_LICENSES.includes(license)
                                                                ) || false,
                                                        };
                                                        createPlaceholderTableRows(subRowLicenses);
                                                    }
                                                });
                                        }
                                        convertToRows(placeholderObjects);
                                        row.toggleRowExpanded();
                                    }}
                                    className={style.subTenantToggleButton}
                                    aria-expanded={row.isExpanded}
                                    data-testid={testIds.common.primaryView.table.expandArrow}
                                >
                                    {createDropdownArrow(row.isExpanded)}
                                </button>
                            )}
                            <TextWithTooltip text={row.original.product} />
                            {isParentLicense && (
                                <>
                                    <button
                                        className={tableStyle.infoIcon}
                                        onClick={() => setTokenPricingTierModalVisible(true)}
                                        type="button"
                                        data-testid={testIds.workArea.license.table.tokenRateButton}
                                    >
                                        <div className={tableStyle.viewRates}>{t("Licenses.viewRates")}</div>
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                );
            },
            width: deriveColumnWidth(30, tableContainerRef),
        });
        columns.push({
            Header: () => <TextWithTooltip text={t("Licenses.licenseType")} key="2" />,
            accessor: "license",
            Cell: ({ row }) => <TextWithTooltip text={row.original.license} />,
            width: deriveColumnWidth(30, tableContainerRef),
        });
        if (tenantDetails.licensingModel === LicensingModel.BUNDLE_WITH_TOKEN) {
            columns.push({
                Header: () => {
                    return (
                        <TextWithTooltip text={t("Licenses.usesLeftTooltip")} key="3" adjustWidth={true}>
                            <div className={tokenTableStyle.info}>
                                {t("Licenses.usesLeft")}
                                <div className={tokenTableStyle.infoText}>
                                    <Info borderColor={theme.contentBackgroundColor} color={theme.textColor} />
                                </div>
                            </div>
                        </TextWithTooltip>
                    );
                },
                accessor: "usesLeft",
                Cell: ({ row }) => {
                    const isSubRow = row.depth > 0;
                    return (
                        <TextWithTooltip
                            text={
                                !isSubRow
                                    ? ""
                                    : deduceUsesLeft(row.original.available, row.original.type, hasBlanccoTokens)
                            }
                        />
                    );
                },
                width: deriveColumnWidth(12, tableContainerRef),
            });
        }
        columns.push({
            Header: () => <TextWithTooltip text={t("Licenses.total")} key="4" />,
            accessor: "total",
            Cell: ({ row }) => {
                const isSubRow = row.depth > 0;
                return (
                    <TextWithTooltip text={isSubRow ? "" : deduceLicenseTotalValue(row.original, hasBlanccoTokens)} />
                );
            },
            width: deriveColumnWidth(12, tableContainerRef),
        });
        columns.push({
            Header: () => <TextWithTooltip text={t("Licenses.remaining")} key="5" />,
            accessor: "available",
            Cell: ({ row }) => {
                const isSubRow = row.depth > 0;
                const hasBlanccoTokens = deducedTierLicenses.length > 0;
                return (
                    <TextWithTooltip
                        content={isSubRow ? "" : deduceLicenseAvailability(row.original, hasBlanccoTokens)}
                        text={isSubRow ? "" : deduceAvailabilityTooltip(row.original, hasBlanccoTokens)}
                    />
                );
            },
            width: deriveColumnWidth(12, tableContainerRef),
        });
        if (!props.own) {
            columns.push({
                Header: () => <TextWithTooltip text={t("Licenses.used")} key="6" />,
                accessor: "used",
                Cell: ({ row }) => {
                    const isSubRow = row.depth > 0;
                    return (
                        <TextWithTooltip
                            text={isSubRow ? "" : deduceLicenseUsedValue(row.original, hasBlanccoTokens)}
                        />
                    );
                },
                width: deriveColumnWidth(14, tableContainerRef),
            });
            columns.push({
                Header: () => <TextWithTooltip text={t("Licenses.overallRemainingLicenses")} key="7" />,
                accessor: "overallRemaining",
                Cell: ({ row }) => {
                    const isSubRow = row.depth > 0;
                    return (
                        <TextWithTooltip
                            text={isSubRow ? "" : deduceLicenseOverallAvailableValue(row.original, hasBlanccoTokens)}
                        />
                    );
                },
                width: deriveColumnWidth(14, tableContainerRef),
            });
        }
        columns.push({
            Header: () => <TextWithTooltip text={t("Licenses.expirationDate")} key="8" />,
            accessor: "expirationDate",
            Cell: ({ row }) =>
                isLicenseExpired(row.original.expirationDate) ? (
                    <div>
                        <span className={classNames(deliveryStyle.statusBadge)}>
                            <StatusBadge values={[statusBadgeTitle(t, row.original.expirationDate)]} tooltip={true} />
                        </span>
                    </div>
                ) : (
                    <DateCell tooltip={true} value={row.original.expirationDate} withoutTime={true} />
                ),
            width: deriveColumnWidth(14, tableContainerRef),
        });
        return columns;
    };

    const columns: Array<Column<LicenseData>> = createColumns();

    const [state, setLicensesData] = React.useState<TableState>({
        licenses: props.initialLicenses.licenseData,
        cursor: props.initialLicenses.cursor,
        scrollPosition: props.scrollPosition,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>(props.requestFailureMessage);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
    const [search, setSearchQuery] = React.useState("");
    const [loading, setLoading] = React.useState<boolean>(false);

    const updateLicenseFlags = (licenses: LicenseData[], tierLicenses: string[]) => {
        const hasLicense = (licenseType: string) =>
            commonLicenses
                ? tierLicenses.includes(licenseType)
                : licenses.some((license) => license.type === licenseType);
        const hasValidationLicenses = hasLicense("bms-validation");
        props.setHasBmsValidationLicenses(hasValidationLicenses);
        const hasBbtiLicenses = hasLicense("bms-bbti");
        props.setHasBmsBbtiLicenses(hasBbtiLicenses);
        const hasInsuranceLicenses = hasLicense("bms-insurance");
        props.setHasBmsInsuranceLicenses(hasInsuranceLicenses);
        const hasUsdkLicenses = hasLicense("usdk");
        props.setHasUsdkLicenses(hasUsdkLicenses);
        const hasNtfLicenses = hasLicense("bms-ntf");
        props.setHasBmsNtfLicenses(hasNtfLicenses);
        const hasAllLicenses = hasLicense("bms-all");
        props.setHasBmsAllLicenses(hasAllLicenses);
        // In tenant access, callCheckBmsLicenses already set hasBmsLicenses on entry.
        // Don't override it here.
        if (!hasTenantCookie()) {
            props.setHasBmsLicenses(
                hasValidationLicenses ||
                    hasBbtiLicenses ||
                    hasInsuranceLicenses ||
                    hasNtfLicenses ||
                    hasAllLicenses ||
                    hasUsdkLicenses
            );
        }
    };

    const fetchData = (initialLoading: boolean) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        licenseService
            .fetchAllLicenses({
                abortController,
                identifier: initialLoading ? "" : state.cursor?.identifier,
                tenantIdentifier: initialLoading ? "" : state.cursor?.tenantIdentifier,
                search,
                own: props.own,
            })
            .then((licenseList) => {
                setLicensesData((prevState) => ({
                    ...prevState,
                    licenses: prevState.licenses.concat(licenseList.licenses.licenseData),
                    scrollPosition: prevState.licenses.length - 1,
                    cursor: licenseList.licenses.cursor,
                }));
                setLoading(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("Licenses.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setInitialLoading(false);
                }
            });
    };

    React.useEffect(() => {
        setLicensesData({
            licenses: [],
            cursor: null,
            scrollPosition: 0,
        });
        fetchData(true);
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [search]);

    React.useEffect(() => {
        if (isLicensingModelBundleWithToken()) {
            tokenRates();
        } else {
            setLoadedTierLicenses(deduceTierLicenses(tenantDetails.tenantTier, true, true));
        }
    }, []);

    React.useEffect(() => {
        if (loading || loadedTierLicenses === undefined || !(props.own || hasTenantCookie())) {
            return;
        }
        updateLicenseFlags(state.licenses, loadedTierLicenses);
    }, [loading, loadedTierLicenses, state.licenses]);

    let dataCount = null;
    if (state.licenses.length > 0) {
        dataCount = t("Licenses.licenseSearchResultHint", { dataCount: state.licenses.length });
    }

    return (
        <>
            <div className={commonStyle.tierBannerContainer}>
                <LicenseTierBanner currentProductsInRateCard={rateCardLicensesAndRates.map((each) => each.productId)} />
            </div>
            <div className={layoutStyle.aboveTable}>
                <div className={layoutStyle.recordCount}>{dataCount}</div>
                <div className={formStyle.search}>
                    <SearchView setSearch={setSearchQuery} searchInProgress={loading} />
                </div>
            </div>
            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                <Table
                    tableIdentity={RepositoryKey.LICENSE_TABLE}
                    data={state.licenses}
                    columns={columns}
                    loaded={!initialLoading}
                    failureMessage={requestFailureMessage}
                    tooltips={true}
                    emptyMessage={t("Licenses.emptyStateMessage", { email: SUPPORT_EMAIL })}
                    testId={testIds.workArea.license.table.itself}
                />
            </div>
            <BundleTokenPricingView
                visibility={tokenPricingTierModalVisible}
                setVisibility={setTokenPricingTierModalVisible}
                productToRate={createProductRates(rateCardLicensesAndRates, deducedTierLicenses, ALL_PRODUCTS)}
            />
            {state.cursor != null &&
                state.licenses.length >= 100 &&
                state.licenses.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
        </>
    );
};

export default connector(LicensesTable);
